import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useApp } from "../../state/AppState"
import { Button, Spinner } from "../../components/ui"
import { AuthLayout } from "./AuthLayout"

/**
 * Where social sign-in lands.
 *
 * The token arrives in the URL **fragment**, not the query string: fragments
 * are never sent to a server, so the token stays out of access logs, proxies
 * and the Referer header. It is read once, adopted, and wiped from the address
 * bar before anything else renders.
 */
export function CallbackPage() {
	const { adoptSession } = useApp()
	const navigate = useNavigate()
	const [error, setError] = useState(null)
	const handled = useRef(false)

	useEffect(() => {
		if (handled.current) return
		handled.current = true

		const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""))
		const token = fragment.get("token")
		const next = fragment.get("next") || "/"

		// Clear the fragment before doing anything that could leave it on screen.
		window.history.replaceState(null, "", window.location.pathname)

		if (!token) {
			setError("That sign-in link did not carry a session. Try again.")
			return
		}

		adoptSession(token)
			.then(() => navigate(next, { replace: true }))
			.catch((cause) => setError(cause.message))
	}, [adoptSession, navigate])

	if (error) {
		return (
			<AuthLayout title="Sign-in did not complete" description={error}>
				<Button variant="primary" className="w-full" onClick={() => navigate("/sign-in")}>
					Back to sign in
				</Button>
			</AuthLayout>
		)
	}

	return (
		<div className="grid min-h-screen place-items-center bg-ink-50">
			<Spinner label="Signing you in" />
		</div>
	)
}
